import React, { useEffect, useState } from "react";
import { useAuth } from "../providers/AuthProvider";

import { WHITELABEL_HOSTS as ENV_HOSTS, WhitelabelEnvironment as Environment } from "../utils/whitelabel";

interface Tool {
  id: string;
  label: string;
  paths: Record<Environment, string>;
}

const TOOLS: Tool[] = [
  { id: "tariffs", label: "Tariffs", paths: { qa: "/tariff-checker", prod: "/tariff-checker" } },
  {
    id: "entity-screening",
    label: "Entity Screening",
    paths: { qa: "/entity-screening", prod: "/entity-screening" },
  },
  { id: "classify", label: "Classify", paths: { qa: "/classify", prod: "/classify" } },
  {
    id: "import-screening",
    label: "Import Screening",
    paths: { qa: "/import-screening", prod: "/import-screening" },
  },
  {
    id: "export-screening",
    label: "Export Screening",
    paths: { qa: "/export-screening", prod: "/export-screening" },
  },
];

interface BrokerToken {
  jwt: string;
  tenantKey: string;
}

export const Tools: React.FC = () => {
  const { token } = useAuth();
  const [environment, setEnvironment] = useState<Environment>("qa");
  const [activeTool, setActiveTool] = useState<string>(TOOLS[0].id);
  const [brokerToken, setBrokerToken] = useState<BrokerToken>();

  // whitelabelqa's own Keycloak login (a cookie-based redirect) breaks once
  // this page is nested inside our cross-origin iframe: browsers blocking
  // third-party cookies drop the login cookie before it's ever set. Instead
  // we mint a short-lived broker JWT for the current athstat user and hand it
  // off via the URL; bite-2.0's /api/v1/partner-sso/broker-login exchanges it
  // for a real session without a cookie-based redirect. Only QA is wired up
  // for this (see bite-test-server's whitelabel-token endpoint) -- prod still
  // uses the plain iframe src.
  useEffect(() => {
    if (environment !== "qa" || !token) {
      return;
    }

    const apiUrl = process.env.REACT_APP_API_URL ?? "";
    fetch(`${apiUrl}/api/v1/whitelabel-token`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`whitelabel-token request failed: ${res.status}`);
        }
        return res.json();
      })
      .then((data: { token: string; tenantKey: string }) =>
        setBrokerToken({ jwt: data.token, tenantKey: data.tenantKey }),
      )
      .catch((err) => {
        console.error("whitelabel-token request failed", err);
        setBrokerToken(undefined);
      });
  }, [environment, token]);

  const tool = TOOLS.find((t) => t.id === activeTool) ?? TOOLS[0];
  const baseUrl = `${ENV_HOSTS[environment]}${tool.paths[environment]}`;
  const url =
    environment === "qa" && brokerToken
      ? `${baseUrl}?jwt=${encodeURIComponent(brokerToken.jwt)}&key=${encodeURIComponent(brokerToken.tenantKey)}`
      : baseUrl;

  return (
    <main className="tools-page">
      <div className="tools-toolbar">
        <div className="tool-tabs">
          {TOOLS.map((t) => (
            <button
              key={t.id}
              className={`tool-tab${t.id === activeTool ? " active" : ""}`}
              onClick={() => setActiveTool(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="toolbar-right">
          <div className="env-toggle">
            {(["qa", "prod"] as Environment[]).map((env) => (
              <button
                key={env}
                className={`env-option${env === environment ? " active" : ""}`}
                onClick={() => setEnvironment(env)}
              >
                {env.toUpperCase()}
              </button>
            ))}
          </div>
          {/* Deliberately opens baseUrl, not the jwt-carrying url: a new window is
              a top-level context where the normal cookie login works, and a second
              broker-login would force-log-out the session the iframe is using. */}
          <button
            className="tool-popout"
            onClick={() => window.open(baseUrl, "_blank", "noopener,noreferrer")}
            title={`Open ${tool.label} in a new window`}
            aria-label={`Open ${tool.label} in a new window`}
          >
            <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
              <path
                d="M14 4h6v6M20 4l-8.5 8.5M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
      <div className="tool-frame-wrapper">
        <iframe key={url} className="tool-frame" src={url} title={tool.label} />
      </div>
    </main>
  );
};
